import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import DashboardShell, { WidgetCard, DonutChart } from "@/components/app/DashboardShell";
import { avatarFor, initialsOf } from "@/lib/avatar";
import {
  TodaysScheduleWidget, TeamActivityWidget, RecentPayslipsWidget, AiTipWidget,
  OrgPreviewWidget,
} from "@/components/app/DashboardWidgets";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [pulse, setPulse] = useState(null);

  const load = async () => {
    try {
      const [a, e, t, ann, hol, p] = await Promise.all([
        api.get("/attendance/mine").catch(() => ({ data: { today: null } })),
        user?.employee_id ? api.get(`/employees/${user.employee_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        api.get("/tasks", { params: { scope: "mine" } }).catch(() => ({ data: [] })),
        api.get("/announcements").catch(() => ({ data: [] })),
        api.get("/holidays").catch(() => ({ data: [] })),
        api.get("/dashboard/announcements").catch(() => ({ data: null })),
      ]);
      setToday(a.data?.today);
      setProfile(e.data);
      setTasks(t.data || []);
      setAnnouncements(ann.data || []);
      setHolidays(hol.data || []);
      setPulse(p.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const checkIn  = async () => { setBusy(true); try { await api.post("/attendance/checkin", {}); toast.success("Checked in"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const checkOut = async () => { setBusy(true); try { await api.post("/attendance/checkout", {}); toast.success("Checked out"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };

  const lb = profile?.leave_balance || { casual: 0, sick: 0, earned: 0 };
  const totalLeave = (lb.casual || 0) + (lb.sick || 0) + (lb.earned || 0);
  const openTasks = tasks.filter(t => t.status !== "done").length;
  const upcoming = holidays.filter(h => new Date(h.date) >= new Date(new Date().toDateString())).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 4);

  // Synthesised payslip data for visual richness — backend pending
  const recentPayslips = [
    { id: "p1", month_label: "Mar 2026", net: 95400 },
    { id: "p2", month_label: "Feb 2026", net: 95400 },
    { id: "p3", month_label: "Jan 2026", net: 92100 },
  ];

  // 6 KPIs matching reference layout
  const kpis = [
    { label: "Today's Attendance", value: today?.check_in ? "Present" : "—", sub: today?.check_in ? `In at ${new Date(today.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Not in yet", delta: { tone: "good", label: today?.check_in ? "Checked in" : "" } },
    { label: "My Tasks",           value: openTasks,                          sub: "pending" },
    { label: "Timesheet",          value: `${(today?.work_hours || 0)}h`,     sub: "this week" },
    { label: "Leaves",             value: totalLeave,                         sub: "balance" },
    { label: "Comp Off",           value: 0,                                  sub: "balance" },
    { label: "Team Score",         value: "4.3/5",                            sub: "Mar 2026", delta: { tone: "good", label: "↑ 8%" } },
  ];

  return (
    <DashboardShell
      greeting={`Good ${greetingTime()}, ${user?.full_name?.split(" ")[0] || "there"}!`}
      subtitle="Here's your day at a glance."
      user={user}
      profile={profile}
      today={today}
      quote={pulse?.quote}
      announcements={announcements}
      onCheckIn={checkIn}
      onCheckOut={checkOut}
      busy={busy}
      kpis={kpis}
      testId="employee-dashboard"
      extraRows={[
        [
          <TodaysScheduleWidget items={[
            { time: "09:30 AM", title: "Daily Standup",       duration: "15m" },
            { time: "11:00 AM", title: "Sprint Planning",     duration: "1h"  },
            { time: "02:00 PM", title: "Code Review · OKR",   duration: "30m" },
            { time: "04:30 PM", title: "1:1 with Vihaan",     duration: "30m" },
          ]} />,
          <TeamActivityWidget items={[
            { name: "Vihaan Mehta", action: "Approved your timesheet", time: "1h ago" },
            { name: "Diya Sharma",  action: "Posted a company update", time: "3h ago" },
            { name: "Aarav Khanna", action: "Welcomed new joiner",     time: "6h ago" },
            { name: "Anya Iyer",    action: "Completed UX course",     time: "1d ago" },
          ]} />,
          <RecentPayslipsWidget payslips={recentPayslips} />,
        ],
        [
          { node: <AiTipWidget />, span: 2 },
          <WidgetCard title="My Profile" testId="emp-profile-mini" action={<Link to="/profile" className="text-[11px] font-semibold text-violet-700 hover:underline">Open →</Link>}>
            <div className="flex flex-col items-center text-center">
              <Avatar className="w-16 h-16 ring-2 ring-violet-100 mb-3"><AvatarImage src={avatarFor(profile)} /><AvatarFallback>{initialsOf(profile?.full_name)}</AvatarFallback></Avatar>
              <div className="text-sm font-bold text-slate-900 font-[Outfit]">{profile?.full_name || user?.full_name}</div>
              <div className="text-[11px] text-slate-500">{profile?.designation || "—"}</div>
              <div className="text-[10px] text-slate-400 mt-1">Employee ID: {profile?.id || "—"}</div>
            </div>
          </WidgetCard>,
        ],
      ]}
      slotLeft={
        <WidgetCard title="My Tasks" action={<Link to="/tasks" className="text-[11px] font-semibold text-violet-700 hover:underline">View all tasks</Link>} testId="emp-tasks-widget">
          {tasks.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-6 text-center">No tasks. 🎉</div>
          ) : (
            <div className="space-y-2.5">
              {tasks.slice(0, 4).map(t => (
                <div key={t.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition">
                  <div className="w-7 h-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center text-[11px] font-bold shrink-0">
                    {(t.title || "T")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">{t.title}</div>
                    <div className="text-[10px] text-slate-500">{t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "—"}</div>
                  </div>
                  <span className={`text-[9.5px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${t.status === "done" ? "bg-emerald-50 text-emerald-700" : t.status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-600"}`}>
                    {(t.status || "todo").replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
      }
      slotMid={
        <WidgetCard title="My Timesheet" action={<Link to="/timesheets" className="text-[11px] font-semibold text-violet-700 hover:underline">View timesheet</Link>} testId="emp-timesheet-widget">
          <div className="flex flex-col items-center py-2">
            <DonutChart
              size={140}
              data={[
                { label: "Billable",     value: 4.5, color: "#7C3AED" },
                { label: "Non-Billable", value: 2.0, color: "#A78BFA" },
              ]}
              centerValue={`${(today?.work_hours || 6.5)}h`}
              centerLabel="Logged"
            />
            <div className="grid grid-cols-2 gap-3 mt-4 w-full">
              <Legend color="#7C3AED" label="Billable"     value="4h 30m (69%)" />
              <Legend color="#A78BFA" label="Non-Billable" value="2h 00m (31%)" />
            </div>
          </div>
        </WidgetCard>
      }
      slotRight={
        <WidgetCard title="Upcoming Holidays" action={<Link to="/calendar" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>} testId="emp-holidays-widget">
          {upcoming.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-4 text-center">No upcoming holidays.</div>
          ) : (
            <div className="space-y-2.5">
              {upcoming.map(h => {
                const d = new Date(h.date);
                return (
                  <div key={h.id} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center text-base">🎉</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{h.name}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 font-semibold">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                  </div>
                );
              })}
            </div>
          )}
        </WidgetCard>
      }
    />
  );
}

function greetingTime() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function Legend({ color, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-semibold text-slate-700">{label}</div>
        <div className="text-[10px] text-slate-500">{value}</div>
      </div>
    </div>
  );
}
